/**
 * Orchestration model for the ExceptionDrawer - all state, effects and
 * derived data, so the drawer file stays layout + wiring.
 *
 * One hook (not two): the chain ``overrides -> combinedEntries ->
 * openEntries -> tag-id lists -> useDrawerObjectActions -> onApplied ->
 * overrides`` is a closed loop, so splitting state from selectors would
 * just re-thread ten inputs through the component. Composes the same
 * sub-hooks the component called inline (``useDrawerNotes``,
 * ``useDrawerObjectActions``, ``useExceptionObjectHistory``,
 * ``useExceptionNoteCounts``) - tests keep mocking the layer below, so
 * this hook is NOT a mock boundary. Extracted as a pure structural move -
 * state, effects and memo dependency arrays are byte-identical to the
 * inline originals.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../../../contexts/AuthContext'
import { canBlockExceptions, canSeeExceptionNotes } from '../../../../services/auth'
import { type NoteRowKey } from '../../../../hooks/useExceptionNotes'
import {
  useExceptionNoteCounts,
  compositeKey,
  type CountKeyParts,
} from '../../../../hooks/useExceptionNoteCounts'
import { useExceptionObjectHistory } from '../../../../hooks/useExceptionObjectHistory'
import { getRowSummaryTitle } from '../../../../utils/exceptionSummary'
import {
  actionableEntries,
  groupEntriesByCode,
  isOpenEntry,
  sortEntriesByStatus,
  sortOpenByDetected,
  type EntryOverride,
  type IssuesChipEntry,
  type SourceLink,
} from '../../../../config/exceptions/shared/issuesState'
import { compareTargetFromRow } from '../../../../config/exceptions/shared/compareSalesTarget'
import type { ExceptionDrawerDetailBlock } from '../../../../config/exceptions/types'
import type { DisplayTimelineEvent } from '../StatusHistoryTimeline'
import { useDrawerObjectActions } from './useDrawerObjectActions'
import { useDrawerNotes } from './useDrawerNotes'
import { buildEyebrow } from './eyebrow'
import type { DrawerTab, ExceptionDrawerProps } from './props'

type DrawerModelArgs = Pick<
  ExceptionDrawerProps,
  | 'open'
  | 'onClose'
  | 'row'
  | 'fpCode'
  | 'getTitle'
  | 'exceptionTypeName'
  | 'getRowTitle'
  | 'getRowSubtitle'
  | 'franchiseData'
  | 'onCountDelta'
  | 'onAfterMutation'
  | 'initialNotesExpanded'
  | 'focusCode'
> & { rowLoading: boolean }

export function useExceptionDrawerModel({
  open,
  onClose,
  row,
  rowLoading,
  fpCode,
  getTitle,
  exceptionTypeName,
  getRowTitle,
  getRowSubtitle,
  franchiseData,
  onCountDelta,
  onAfterMutation,
  initialNotesExpanded = false,
  focusCode,
}: DrawerModelArgs) {
  const { user } = useAuth()
  const notesGated = canSeeExceptionNotes(user)
  const actionsGated = canBlockExceptions(user)

  const key: NoteRowKey | null =
    row && row.exception_code && row.object_id && row.data_domain && fpCode
      ? {
          fp_code: fpCode,
          exception_code: row.exception_code,
          data_domain: row.data_domain,
          object_id: String(row.object_id),
        }
      : null

  // Object identity for the notes feed - present even when there is no current
  // exception code (object-only drawer on a zero-issue browse row), so the
  // object's existing comments still load (via the siblings endpoint with no
  // exclusion). Posting stays gated on ``key`` (a note needs an exception).
  const objectNotesKey =
    notesGated && row?.object_id && row?.data_domain && fpCode
      ? {
          fp_code: fpCode,
          data_domain: row.data_domain as string,
          object_id: String(row.object_id),
        }
      : null

  // Object-level Activity: one eager fetch spanning every visible code on the
  // record (Evo/PSC only - gated on notesGated). Feeds the Activity tab and the
  // action line's oldest-open age. FP users get null (no fetch).
  const objectHistoryKey =
    notesGated && row?.object_id && row?.data_domain && fpCode
      ? {
          fp_code: fpCode,
          data_domain: row.data_domain as string,
          object_id: String(row.object_id),
          // Threaded so the server rolls the timeline up to the whole product
          // for the composite per-outlet stock codes (see object_family_match).
          exception_code: row.exception_code as string | undefined,
        }
      : null
  const {
    events: objectEvents,
    lastConfirmedAt,
    oldestOpenDetectedAt,
    loading: historyLoading,
    settled: historySettled,
    error: historyError,
    refresh: refreshHistory,
  } = useExceptionObjectHistory(objectHistoryKey)

  // Tabbed body. Open issues is the default; opening from a note affordance
  // lands on Notes, and opening into a closed code lands on Closed issues.
  // ``notesActivated`` makes the Notes feed lazy - it stays false (no fetch)
  // until the Notes tab is first opened, then sticks for the session.
  const [activeTab, setActiveTab] = useState<DrawerTab>('open')
  const [notesActivated, setNotesActivated] = useState(false)
  const handleTabChange = useCallback((next: DrawerTab) => {
    setActiveTab(next)
    if (next === 'notes') setNotesActivated(true)
  }, [])

  // The notes feed + composer + expansion all live in this hook. ``active``
  // gates the (current + sibling) note fetches until the Notes tab is opened.
  const notes = useDrawerNotes({
    key,
    objectKey: objectNotesKey,
    notesGated,
    open,
    active: notesActivated,
    exceptionTypeName,
    onCountDelta,
  })

  // Eager Notes-tab badge count. Reuses the same bulk note-counts endpoint the
  // tables use, keyed on the object tuple, so the badge is accurate from first
  // paint on BOTH the inspector and the admin host (both supply the tuple) -
  // not a lazy grey-until-opened guess. ``counts`` is this exception's notes,
  // ``siblingCounts`` the same-object/other-type notes, matching the merged
  // feed's ``noteCount``. Evo/PSC-gated by the hook, like the feed itself.
  const noteCountRows = useMemo<CountKeyParts[]>(
    () =>
      notesGated && row?.exception_code && row?.object_id && row?.data_domain
        ? [
            {
              exception_code: row.exception_code as string,
              data_domain: row.data_domain as string,
              object_id: String(row.object_id),
            },
          ]
        : [],
    [notesGated, row?.exception_code, row?.object_id, row?.data_domain],
  )
  const {
    counts: eagerNoteCounts,
    siblingCounts: eagerSiblingNoteCounts,
    settled: noteCountsSettled,
  } = useExceptionNoteCounts(fpCode, noteCountRows)
  const eagerNoteCount =
    noteCountRows.length > 0
      ? (eagerNoteCounts[compositeKey(noteCountRows[0])] ?? 0) +
        (eagerSiblingNoteCounts[compositeKey(noteCountRows[0])] ?? 0)
      : 0
  // Once the Notes tab has opened AND its live feed has finished loading, the
  // live count is authoritative (post create/delete); fall back to the eager
  // count before then so the badge never flicks to 0 mid-fetch.
  const notesBadgeCount =
    notesActivated && !notes.loading && !notes.siblingLoading
      ? notes.noteCount
      : eagerNoteCount

  // Set by any object-action mutation (via objectActions.onMutated). The
  // close-side effect reads it so the host list refetches once on close.
  const [annotationDirty, setAnnotationDirty] = useState(false)

  // Optimistic per-tag status overrides. The source ``row.errors`` only
  // refetches when the drawer closes, so we overlay each applied change here to
  // reflect a mutation in the list immediately. Keyed by ``tag_id``, reset per
  // row (see the open/row-change effect below).
  const [overrides, setOverrides] = useState<Record<number, EntryOverride>>({})
  const applyEntryOverride = useCallback(
    (tagIds: number[], change: EntryOverride) => {
      setOverrides((prev) => {
        const next = { ...prev }
        for (const id of tagIds) next[id] = { ...next[id], ...change }
        return next
      })
    },
    [],
  )

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Lock body horizontal overflow while the drawer is mounted. The Slide
  // animation transitions the panel from translateX(100%) to 0, which on
  // pages whose table already scrolls horizontally can briefly bleed past
  // the viewport's right edge and surface an unwanted scrollbar.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflowX
    document.body.style.overflowX = 'hidden'
    return () => {
      document.body.style.overflowX = previous
    }
  }, [open])

  // Reset tab + per-row dirty on a new row / re-open. The notes hook owns its
  // own per-row reset. Opening from a note affordance lands on Notes (and
  // activates the lazy feed); opening into a code that is currently closed
  // lands on Closed issues; everything else starts on Open issues.
  useEffect(() => {
    if (!open) return
    // Derive the closed-landing decision from the RAW row payload (not the
    // memoized ``closedEntries``, which moves with optimistic overrides and
    // would reset the tab mid-session). The focused code counts as closed only
    // when it sits in the closed payload and NOT in the open one.
    const focused = row?.exception_code as string | undefined
    const rawOpen = (row?.errors ?? []) as IssuesChipEntry[]
    const rawClosed = (row?.recently_closed_errors ?? []) as IssuesChipEntry[]
    const openedFromIsClosed =
      !!focused &&
      rawClosed.some((e) => e.code === focused) &&
      !rawOpen.some((e) => e.code === focused)
    const startTab: DrawerTab =
      initialNotesExpanded && notesGated
        ? 'notes'
        : openedFromIsClosed
          ? 'closed'
          : 'open'
    setActiveTab(startTab)
    setNotesActivated(startTab === 'notes')
    setAnnotationDirty(false)
    setOverrides({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.exception_code, row?.object_id, initialNotesExpanded, notesGated])

  // Close-side effect: when the drawer goes open->closed, fire onAfterMutation
  // once if any mutation happened this session so the host list refetches once.
  useEffect(() => {
    if (open) return
    if (annotationDirty) onAfterMutation?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onAfterMutation, annotationDirty])

  const errors = row?.errors as IssuesChipEntry[] | undefined
  const rawRecentlyClosed = row?.recently_closed_errors as
    | IssuesChipEntry[]
    | undefined
  // Same umbrella collapse on the closed side, so the Closed issues tab doesn't
  // double-count a resolved umbrella alongside its resolved discrete sibling.
  const recentlyClosed = useMemo(
    () => actionableEntries(rawRecentlyClosed),
    [rawRecentlyClosed],
  )
  // Centralised source deep-links (backend build_source_links) for the
  // header's source-link row.
  const sourceLinks = (row?.source_links ?? []) as SourceLink[]
  // API-driven compare target (backend build_compare_sales_target). When
  // present, the header renders a "Compare sales" action beside the
  // source links - both the inspector and admin drawers resolve rows
  // through the same stamped payload.
  const compareSalesTarget = useMemo(
    () => (row ? compareTargetFromRow(row) : null),
    [row],
  )

  // The weight-0 umbrella roll-up is a headline, not a countable issue, so it's
  // collapsed out of the open list - EXCEPT when it's the sole flag (date /
  // client / line-item drift with no discrete code), where it stands in as the
  // single actionable issue. ``actionableEntries`` is shared with the table
  // chip so the count never disagrees across surfaces.
  const { umbrellaError, actionableOpenErrors } = useMemo(() => {
    const open = errors ?? []
    return {
      umbrellaError: open.find((e) => e.is_umbrella),
      actionableOpenErrors: actionableEntries(open),
    }
  }, [errors])
  // One unified, status-sorted list: open issues + recently resolved/ignored,
  // each carrying its own chip/colour (Blocked, Needs review, Marked fixed,
  // Ignored, Resolved). Optimistic overrides are overlaid first so a just-
  // mutated row reflects its new status without waiting for the on-close
  // refetch.
  const combinedEntries = useMemo(() => {
    const withOverride = (e: IssuesChipEntry): IssuesChipEntry => {
      const o = e.tag_id != null ? overrides[e.tag_id] : undefined
      return o ? { ...e, ...o } : e
    }
    const merged = [
      ...actionableOpenErrors.map(withOverride),
      ...(recentlyClosed ?? []).map(withOverride),
    ]
    return sortEntriesByStatus(merged)
  }, [actionableOpenErrors, recentlyClosed, overrides])

  // Open (still-actionable) subset drives the action line, the "Can't fix yet"
  // set and the Open issues tab badge; the closed subset feeds the Closed
  // issues tab. Both derive from the same overlaid list so an optimistic
  // ignore/reopen moves a row between tabs immediately. The open list is
  // ordered oldest-detected first (``sortOpenByDetected``) so status no longer
  // reorders it; the closed list keeps its status order.
  // Flat (per-tag) lists - the ground truth for tag-id collection (bulk
  // actions), the synthetic activity events, and per-outlet aggregation.
  const openFlat = useMemo(
    () => sortOpenByDetected(combinedEntries.filter(isOpenEntry)),
    [combinedEntries],
  )
  const closedFlat = useMemo(
    () => combinedEntries.filter((e) => !isOpenEntry(e)),
    [combinedEntries],
  )
  // Auto-expand / float-to-top focus: the explicit ``focusCode`` an
  // exception-view mount passes (its current-exception bias). A no-bias surface
  // (Data Explorer / admin DQ Workbench) passes none, so nothing is forced open.
  // If the focus is the weight-0 umbrella, redirect to the primary discrete
  // problem (never the phantom umbrella row).
  const resolvedFocus = useMemo(() => {
    if (!focusCode) return undefined
    if (umbrellaError && focusCode === umbrellaError.code) {
      return openFlat.find((e) => e.primary)?.code ?? combinedEntries[0]?.code
    }
    return focusCode
  }, [focusCode, umbrellaError, openFlat, combinedEntries])

  // Grouped (one row per problem code) - what the Issues list renders and the
  // tab badges count. A same-code-at-N-outlets rollup collapses to one row
  // badged "· N outlets"; a no-op where codes are unique (sales / clients).
  // When focused, that problem floats to the top of the otherwise
  // oldest-detected order so the user lands on the issue they clicked.
  const openEntries = useMemo(() => {
    const grouped = groupEntriesByCode(openFlat)
    if (!resolvedFocus) return grouped
    const idx = grouped.findIndex((e) => e.code === resolvedFocus)
    if (idx <= 0) return grouped
    const reordered = [...grouped]
    const [focused] = reordered.splice(idx, 1)
    reordered.unshift(focused)
    return reordered
  }, [openFlat, resolvedFocus])
  const closedEntries = useMemo(
    () => groupEntriesByCode(closedFlat),
    [closedFlat],
  )
  const hasOpen = openEntries.length > 0

  // ``focusedCode`` stays the backend-stamped code on the row - it drives the
  // eager Why/How (its detail_blocks rode the envelope) and the closed-tab
  // landing, regardless of whether the surface has a current-exception bias.
  const focusedCode = row?.exception_code as string | undefined

  // The row to auto-expand: the resolved focus (exception-view bias), else the
  // SOLE open problem when there's exactly one, else nothing - so a no-bias
  // surface with several problems opens with everything collapsed.
  const listOpenedFrom =
    resolvedFocus ??
    (openEntries.length === 1 ? openEntries[0].code : undefined)

  // Activity feed = backend lifecycle events + a derived "Fix not confirmed
  // by sync" entry per open entry whose latest sync still detected it after a
  // mark-fixed (``fix_confirmation_state === 'still_detected'``). Derived
  // frontend-side until the activity-feed follow-up makes it a real event.
  const displayEvents = useMemo<DisplayTimelineEvent[]>(() => {
    const synthetic: DisplayTimelineEvent[] = openFlat
      .filter(
        (e) =>
          e.fix_confirmation_state === 'still_detected' &&
          e.tag_id != null &&
          e.last_detected_at,
      )
      .map((e) => ({
        kind: 'fix_not_confirmed' as const,
        code: e.code,
        name: e.label,
        occurred_at: e.last_detected_at as string,
        tag_id: e.tag_id as number,
        reason: null,
        actor: null,
      }))
    if (synthetic.length === 0) return objectEvents
    // Keep the backend's ASC ordering contract - the timeline reverses
    // client-side. Date-parse both sides (backend events are tz-suffixed,
    // entry timestamps naive-UTC) rather than string-comparing mixed formats.
    return [...objectEvents, ...synthetic].sort(
      (a, b) =>
        new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
    )
  }, [objectEvents, openFlat])

  // Activity tab badge: total events shown in the feed (incl. derived ones)
  // so the badge always matches the list.
  const activityCount = displayEvents.length

  // Open, NOT-yet-blocked tag ids - "Can't fix yet" blocks exactly these
  // (already-blocked rows keep their existing reason).
  const blockableTagIds = useMemo(
    () =>
      openFlat
        .filter((e) => !e.blocked)
        .map((e) => e.tag_id)
        .filter((id): id is number => typeof id === 'number'),
    [openFlat],
  )
  // Every open tag id - "I've fixed all N" stamps these, so the overlay marks
  // them all fixed at once. From the flat list so per-outlet tags all count.
  const allOpenTagIds = useMemo(
    () =>
      openFlat
        .map((e) => e.tag_id)
        .filter((id): id is number => typeof id === 'number'),
    [openFlat],
  )

  // The drawer's mutation engine: object-level action line + per-row kebab.
  // Only wired into the UI for Evo/PSC (actionsGated); FP users never see the
  // affordances that call it.
  const objectActions = useDrawerObjectActions({
    fpCode,
    dataDomain: (row?.data_domain as string) ?? '',
    objectId: row?.object_id != null ? String(row.object_id) : '',
    exceptionCode: (row?.exception_code as string) ?? '',
    blockableTagIds,
    allOpenTagIds,
    franchiseData,
    onMutated: () => {
      setAnnotationDirty(true)
      void refreshHistory()
    },
    onApplied: applyEntryOverride,
  })

  // Untracked product health-warnings (margin highs/lows) that show in the
  // table's Issues chip but carry no lifecycle tag (backend
  // ``non_lifecycle_warning_count``). Surfaced so the drawer never implies its
  // fixable count equals that chip.
  const nonLifecycleWarningCount = Number(row?.non_lifecycle_warning_count ?? 0)
  // A product whose only flags are untracked warnings would open an empty
  // fix-job; show a healthy "nothing to fix here" state instead.
  const emptyHealthy =
    !rowLoading && combinedEntries.length === 0 && nonLifecycleWarningCount > 0
  const eyebrow = buildEyebrow(
    row?.data_domain,
    row?.exception_code as string | undefined,
  )

  // Single readiness gate: the drawer body renders all-at-once behind one
  // skeleton instead of pop-in-as-it-lands. Ready when the row is resolved
  // AND every eager fetch for THIS row has settled - object history (Activity
  // badge, oldest-open age, last-sync line) and the bulk note counts (Notes
  // badge, note dots). Each clause short-circuits when its fetch will never
  // fire (FP users: ``objectHistoryKey`` is null and ``noteCountRows`` is
  // empty), and the hooks settle in ``finally`` so an endpoint error can
  // never freeze the skeleton.
  const bodyReady =
    !rowLoading &&
    row != null &&
    (objectHistoryKey == null || historySettled) &&
    (noteCountRows.length === 0 || noteCountsSettled)

  const title = row ? (getTitle?.(row) ?? getRowSummaryTitle(row)) : ''
  const rowSummary = row ? (getRowTitle?.(row) ?? title) : ''
  const rowSubtitle = row ? getRowSubtitle?.(row) : undefined
  const rowSubtitleText =
    typeof rowSubtitle === 'string' && rowSubtitle.trim() !== ''
      ? rowSubtitle
      : undefined

  // Identity string for the row. Passed to the list as its key so it remounts
  // (and re-runs its auto-expand) when the user clicks a different row while
  // the drawer stays mounted.
  const rowResetKey = `${row?.exception_code ?? ''}|${row?.object_id ?? ''}`

  // "Why this was flagged" blocks are built on the backend per code and
  // ride on the row (see backend ``apply_detail_blocks``) for the focused code.
  // Passed to IssuesEntryList so the opened-from row renders them eagerly.
  const detailsBlocks: ExceptionDrawerDetailBlock[] | undefined =
    row?.detail_blocks

  return {
    user,
    notesGated,
    actionsGated,
    notes,
    objectActions,
    activeTab,
    handleTabChange,
    displayEvents,
    lastConfirmedAt,
    oldestOpenDetectedAt,
    historyLoading,
    historyError,
    sourceLinks,
    compareSalesTarget,
    combinedEntries,
    openEntries,
    closedEntries,
    hasOpen,
    blockableTagIds,
    focusedCode,
    listOpenedFrom,
    activityCount,
    notesBadgeCount,
    emptyHealthy,
    bodyReady,
    eyebrow,
    rowSummary,
    rowSubtitleText,
    rowResetKey,
    detailsBlocks,
  }
}
